// Getting reports off a blocked device (docs/10 §5) — scheduling, the delivery
// ladder, backoff, and the consent gate that stands in front of all of it.
//
// The devices whose logs matter most are the ones that cannot reach the server,
// so delivery is a LADDER tried in order, and the app descends only as far as
// it must:
//
//   1. the socket that is already open (RPC-over-WS) — the failure we actually
//      hit had a live WSS connection the whole time, and a request that rides an
//      established flow pays no new handshake, which is the thing that was
//      failing;
//   2. any reachable mirror, riding the REST endpoint failover;
//   3. a separate intake on independent infrastructure, from the signed
//      `diag` array — it must not share a failure domain with the API;
//   4. store and forward, which is the queue itself, and which covers far more
//      real cases than any clever live channel;
//   5. the user, via preview + copy — the floor, and the only rung that always
//      works. This module makes rung 5 possible by never sending anything the
//      user could not have read first.
//
// A rung that answers `null` is not a failure, it is an absence (no socket, no
// intake in the list): the ladder simply steps down without spending an attempt.

import {
  isSafeToUpload,
  renderUpload,
  type DeliveryOutcome,
  type DeliveryVia,
  type QueuedReport,
} from './report.js';
import type { ReportQueue } from './queue.js';
import type { Consent } from './consent.js';

/** First retry lands about a minute out; doubling from there. */
export const UPLOAD_BACKOFF_BASE_MS = 60_000;
/** Ceiling. A device blocked for a week retries hourly-ish, not never. */
export const UPLOAD_BACKOFF_CAP_MS = 6 * 60 * 60 * 1000;
/** Idle poll while something is queued but the clock has not come round. */
export const UPLOAD_IDLE_MS = 5 * 60_000;
/**
 * Nothing leaves within this long of a boot. A report is worth nothing next to
 * a call connecting, and a burst of uploads at launch is also the most
 * distinctive traffic shape the app could possibly emit (docs/10 §8).
 */
export const UPLOAD_SETTLE_MS = 30_000;
/** Reports one flush may deliver before yielding to the next scheduled one. */
export const MAX_REPORTS_PER_FLUSH = 4;

/** What a rung is handed. `bytes` is exactly what `preview()` shows. */
export interface UploadPayload {
  reportId: string;
  bytes: string;
  /** gzip of `bytes`, or null when no compressor exists (a bare JSC context
   *  has no CompressionStream). A transport encoding only — never a different
   *  document from the one the user previewed. */
  gzip: Uint8Array | null;
}

export interface DiagSender {
  via: DeliveryVia;
  /** `null` ⇒ this rung is unavailable right now; try the next one, and do not
   *  count it as an attempt. */
  send(payload: UploadPayload): Promise<DeliveryOutcome | null>;
}

export interface UploaderOptions {
  queue: ReportQueue;
  consent: Consent;
  /** Re-read per flush: the ladder changes as the endpoint list rotates. */
  senders: () => readonly DiagSender[];
  /** Uploads are NEVER sent during a call (docs/10 §5). */
  inCall: () => boolean;
  now?: () => number;
  random?: () => number;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
  /** gzip, "if available". Defaults to CompressionStream when the host has it. */
  compress?: ((bytes: string) => Promise<Uint8Array | null>) | null;
  log?: (level: string, msg: string) => void;
  settleMs?: number;
}

export class Uploader {
  private readonly queue: ReportQueue;
  private readonly consent: Consent;
  private readonly senders: () => readonly DiagSender[];
  private readonly inCall: () => boolean;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;
  private readonly compress: ((bytes: string) => Promise<Uint8Array | null>) | null;
  private readonly log: (level: string, msg: string) => void;
  private readonly settleMs: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private stopped = false;
  private readonly startedAt: number;
  private lastOutcome: DeliveryOutcome | null = null;

  constructor(options: UploaderOptions) {
    this.queue = options.queue;
    this.consent = options.consent;
    this.senders = options.senders;
    this.inCall = options.inCall;
    this.now = options.now ?? Date.now;
    this.random = options.random ?? Math.random;
    this.setTimeoutFn = options.setTimeoutFn ?? setTimeout;
    this.clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout;
    this.compress = options.compress === undefined ? defaultCompressor() : options.compress;
    this.log = options.log ?? ((): void => undefined);
    this.settleMs = options.settleMs ?? UPLOAD_SETTLE_MS;
    this.startedAt = this.now();
  }

  /** Arm the next flush, jittered. Idempotent — a second call does not stack. */
  schedule(delayMs?: number): void {
    if (this.stopped || this.timer !== null) return;
    const base = delayMs ?? this.nextDelayMs();
    // Full jitter on the back half: two devices that failed at the same instant
    // (a carrier-wide block, which is exactly when reports matter) must not
    // retry in lockstep and look like a signature.
    const jittered = Math.max(0, Math.round(base * (0.5 + 0.5 * this.random())));
    this.timer = this.setTimeoutFn(() => {
      this.timer = null;
      void this.flush().then(() => {
        if (this.queue.size() > 0) this.schedule();
      });
    }, jittered);
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
  }

  /** The outcome of the most recent attempt, for the snapshot slice. */
  lastDeliveryOutcome(): DeliveryOutcome | null {
    return this.lastOutcome;
  }

  /**
   * The exact bytes that would be uploaded, or null when there is nothing
   * queued (or what is queued failed the §3 gate and was dropped). Callers may
   * show this to the user and may copy it — rung 5.
   *
   * With no `id` this addresses the oldest queued report. Callers that have a
   * particular one in mind pass its id; `DiagCenter` always does, so that the
   * preview, the "send" that follows it and the copy-out are provably about one
   * document (see `DiagCenter.targetId`).
   */
  async preview(id?: string): Promise<UploadPayload | null> {
    const report = id === undefined ? this.queue.list()[0] : this.queue.list().find((r) => r.id === id);
    if (!report) return null;
    return this.render(report);
  }

  /**
   * Try to deliver. Returns quietly in every case where sending would be wrong:
   * during a call, before the settle window, without consent, with nothing due.
   */
  async flush(): Promise<void> {
    if (this.running || this.stopped) return;
    this.running = true;
    try {
      await this.drain();
    } finally {
      this.running = false;
    }
  }

  private async drain(): Promise<void> {
    // "Never sent during a call" is checked here AND immediately before each
    // send: a call can start while a body is being compressed.
    if (this.inCall()) return;
    if (this.now() - this.startedAt < this.settleMs) return;

    // Reports the consent gate refuses. They are SKIPPED, not a stop: outside
    // dev mode the queue routinely holds unconsented reports, and one of them
    // sitting at the head must not starve the report the user just tapped
    // "send" on. Grows monotonically, and the queue is finite, so the loop
    // terminates.
    const impermissible = new Set<string>();
    // Bounded: a flush drains a few reports, not a queue. A device that comes
    // back after a night of blocking must not answer with a burst — §8 asks for
    // traffic that does not identify the app, and a backlog fired at once is
    // the most distinctive shape it could take.
    let drained = 0;
    while (drained < MAX_REPORTS_PER_FLUSH) {
      const report = this.queue.next(this.now(), impermissible);
      if (!report) return;
      if (!this.permitted(report)) {
        impermissible.add(report.id);
        continue;
      }
      drained += 1;
      const payload = await this.render(report);
      if (payload === null) {
        // Failed the §3 gate: dropped, never sent, and said out loud.
        this.log('error', 'diag: report withheld — failed the redaction gate');
        await this.queue.remove(report.id);
        continue;
      }
      if (this.inCall() || this.stopped) return;

      const outcome = await this.deliver(payload);
      if (outcome === null) {
        // No rung was usable at all. Not an attempt; wait and try later.
        this.lastOutcome = 'unavailable';
        return;
      }
      this.lastOutcome = outcome.outcome;
      if (outcome.outcome === 'ok') {
        await this.queue.remove(report.id);
        this.log('info', `diag: report delivered via ${outcome.via}`);
        continue;
      }
      await this.queue.noteAttempt(report.id, {
        atMs: this.now(),
        via: outcome.via,
        outcome: outcome.outcome,
        nextAtMs: this.now() + this.backoffMs(report.attempts + 1),
      });
      this.log('warn', `diag: report ${outcome.outcome} via ${outcome.via}, will retry`);
      return;
    }
  }

  /** Consent gate (§4). Nothing here can be talked round by a caller. */
  private permitted(report: QueuedReport): boolean {
    if (report.consented) return this.consent.allowsConsented();
    return this.consent.allowsAutomatic();
  }

  private async render(report: QueuedReport): Promise<UploadPayload | null> {
    const bytes = renderUpload(report);
    if (!isSafeToUpload(bytes)) return null;
    const gzip = this.compress === null ? null : await this.compress(bytes).catch(() => null);
    return { reportId: report.id, bytes, gzip };
  }

  private async deliver(
    payload: UploadPayload,
  ): Promise<{ via: DeliveryVia; outcome: DeliveryOutcome } | null> {
    let attempted: { via: DeliveryVia; outcome: DeliveryOutcome } | null = null;
    for (const sender of this.senders()) {
      if (this.inCall() || this.stopped) return attempted;
      let outcome: DeliveryOutcome | null;
      try {
        outcome = await sender.send(payload);
      } catch {
        outcome = 'other';
      }
      if (outcome === null) continue; // rung unavailable — step down, no attempt
      attempted = { via: sender.via, outcome };
      if (outcome === 'ok') return attempted;
    }
    return attempted;
  }

  private nextDelayMs(): number {
    const earliest = this.queue.earliestAtMs();
    if (earliest === null) return UPLOAD_IDLE_MS;
    return Math.max(0, earliest - this.now()) || UPLOAD_IDLE_MS;
  }

  /** Exponential, capped. The jitter is applied by `schedule`. */
  private backoffMs(attempts: number): number {
    const exponent = Math.min(Math.max(attempts, 1) - 1, 20);
    return Math.min(UPLOAD_BACKOFF_BASE_MS * 2 ** exponent, UPLOAD_BACKOFF_CAP_MS);
  }
}

/**
 * gzip through the platform's own CompressionStream, when there is one. The
 * bare JSContext the iOS brain runs in has none, and that is fine: the bodies
 * are a few KB and §8 forbids anything that would make the traffic distinctive,
 * which a missing Content-Encoding certainly is not.
 */
export function defaultCompressor(): ((bytes: string) => Promise<Uint8Array | null>) | null {
  const CS = (globalThis as { CompressionStream?: new (format: string) => unknown })
    .CompressionStream;
  if (typeof CS !== 'function' || typeof Response === 'undefined' || typeof TextEncoder === 'undefined') {
    return null;
  }
  return async (bytes: string): Promise<Uint8Array | null> => {
    try {
      const stream = new CS('gzip') as { readable: ReadableStream; writable: WritableStream };
      const writer = (stream.writable as WritableStream<Uint8Array>).getWriter();
      void writer.write(new TextEncoder().encode(bytes));
      void writer.close();
      const buffer = await new Response(stream.readable as BodyInit).arrayBuffer();
      return new Uint8Array(buffer);
    } catch {
      return null;
    }
  };
}
